<div class="coding-answer cw" [attr.data-theme]="theme" [class.fresh]="fresh" *ngIf="answer as a">

  <div class="cw-mode">
    <span class="cw-mode-icon" aria-hidden="true">&lt;/&gt;</span>
    <span>Coding mode</span>
    <span class="cw-sep" aria-hidden="true">&middot;</span>
    <span>{{ labels[language] }}</span>
    <ng-container *ngIf="!fresh && edits && !a.clarification && !a.streaming">
      <span class="cw-sep" aria-hidden="true">&middot;</span>
      <span>{{ edits }} line{{ edits === 1 ? '' : 's' }} to change</span>
    </ng-container>
    <ng-container *ngIf="a.transcribed && !a.clarification">
      <span class="cw-sep" aria-hidden="true">&middot;</span>
      <span>code read from your screenshot</span>
    </ng-container>
    <ng-container *ngIf="a.streaming">
      <span class="cw-sep" aria-hidden="true">&middot;</span>
      <span class="cw-writing">writing<span class="cw-dots" aria-hidden="true"></span></span>
    </ng-container>
  </div>

  <p class="cw-clarify" *ngIf="a.clarification">
    <strong>Needs a clearer input.</strong> {{ a.clarification }}
    <span class="cw-muted" *ngIf="!a.streaming">Nothing was changed; retake the screenshot or add detail and send again.</span>
  </p>

  <p class="cw-summary" *ngIf="a.summary && !fresh"><ng-container *ngFor="let part of a.summary | codeSpans"><code *ngIf="part.code">{{ part.text }}</code><ng-container *ngIf="!part.code">{{ part.text }}</ng-container></ng-container></p>

  <!-- A new question still streaming in: the code as it is typed, in the block the finished answer uses. -->
  <ng-container *ngIf="fresh && a.streaming">
    <div class="cw-file" *ngFor="let f of streamFiles; trackBy: trackIndex">
      <div class="cw-filename" *ngIf="streamFiles.length > 1">{{ f.path }}</div>
      <pre class="language-{{ f.language }} cw-stream"><code class="language-{{ f.language }}" [innerHTML]="f.html"></code></pre>
    </div>
  </ng-container>

  <!-- A fresh solution: the ordinary code answer, nothing more. The explanation is one click away. -->
  <ng-container *ngIf="fresh && !a.streaming">
    <div class="cw-file" *ngFor="let f of files">
      <div class="cw-filename" *ngIf="files.length > 1">{{ f.path }}</div>
      <pre class="language-{{ f.language }}"><code class="language-{{ f.language }}" [innerHTML]="f.fullHtml"></code></pre>
    </div>
    <ng-container *ngIf="a.summary || hasNotes">
      <button type="button" class="cw-link" (click)="showNotes = !showNotes">{{ showNotes ? 'Hide explanation' : 'Explain the code' }}</button>
      <div class="cw-explanation" *ngIf="showNotes">
        <p class="cw-summary" *ngIf="a.summary"><ng-container *ngFor="let part of a.summary | codeSpans"><code *ngIf="part.code">{{ part.text }}</code><ng-container *ngIf="!part.code">{{ part.text }}</ng-container></ng-container></p>
        <ng-container *ngFor="let f of files">
          <div class="cw-filename" *ngIf="files.length > 1 && f.notes.length">{{ f.path }}</div>
          <ul class="cw-notes" *ngIf="f.notes.length">
            <li *ngFor="let note of f.notes"><ng-container *ngFor="let part of note | codeSpans"><code *ngIf="part.code">{{ part.text }}</code><ng-container *ngIf="!part.code">{{ part.text }}</ng-container></ng-container></li>
          </ul>
        </ng-container>
      </div>
    </ng-container>
  </ng-container>

  <!-- A change to existing code: the diff view, growing line by line while it streams in. -->
  <ng-container *ngIf="!fresh">
    <p class="cw-muted cw-reading" *ngIf="streamReading">Reading your code from the screenshot&hellip;</p>
    <nav class="cw-tabs" aria-label="Files" *ngIf="files.length > 1">
      <button *ngFor="let f of files; trackBy: trackFile" type="button" class="cw-tab" [class.selected]="f.id === selectedFileId"
        [attr.aria-pressed]="f.id === selectedFileId" (click)="select(f.id)">
        {{ f.path }}<span class="cw-tab-badge" *ngIf="f.status !== 'unchanged'">{{
          f.status === 'new' ? 'new' : f.status === 'deleted' ? 'deleted' : f.status === 'renamed' ? 'renamed'
          : f.edits + (f.edits === 1 ? ' line' : ' lines') }}</span>
      </button>
    </nav>

    <div class="cw-file" *ngIf="selected as f">
      <div class="cw-filename">
        <span>{{ f.path }}</span>
        <span class="cw-badge" *ngIf="f.status === 'renamed'">renamed from {{ f.before?.path }}</span>
        <span class="cw-badge" *ngIf="f.status === 'new'">new file</span>
        <span class="cw-badge del" *ngIf="f.status === 'deleted'">delete this file</span>
      </div>
      <p class="cw-note file" *ngFor="let note of f.notes"><ng-container *ngFor="let part of note | codeSpans"><code *ngIf="part.code">{{ part.text }}</code><ng-container *ngIf="!part.code">{{ part.text }}</ng-container></ng-container></p>

      <div class="diff-view cw-diff" [class.cw-diff-streaming]="a.streaming" *ngIf="f.hunks.length && (f.edits || f.status === 'deleted' || a.streaming || noChanges)">
        <ng-container *ngFor="let hunk of f.hunks; trackBy: trackHunk">
          <ng-container *ngIf="hunk.kind === 'context'">
            <ng-container *ngTemplateOutlet="rows; context: { $implicit: hunk.head }"></ng-container>
            <button type="button" class="cw-fold" *ngIf="hunk.hidden.length && !hunk.expanded" (click)="hunk.expanded = true">&#8943; {{ hunk.hidden.length }} unchanged lines</button>
            <ng-container *ngIf="hunk.hidden.length && hunk.expanded">
              <ng-container *ngTemplateOutlet="rows; context: { $implicit: hunk.hidden }"></ng-container>
            </ng-container>
            <ng-container *ngTemplateOutlet="rows; context: { $implicit: hunk.tail }"></ng-container>
          </ng-container>
          <ng-container *ngIf="hunk.kind === 'change'">
            <ng-container *ngTemplateOutlet="rows; context: { $implicit: hunk.rows }"></ng-container>
            <div class="cw-explain" *ngIf="hunk.steps.length || hunk.notes.length">
              <p class="cw-step" *ngFor="let step of hunk.steps"><ng-container *ngFor="let part of step | codeSpans"><code *ngIf="part.code">{{ part.text }}</code><ng-container *ngIf="!part.code">{{ part.text }}</ng-container></ng-container></p>
              <p class="cw-why" *ngFor="let note of hunk.notes"><ng-container *ngFor="let part of note | codeSpans"><code *ngIf="part.code">{{ part.text }}</code><ng-container *ngIf="!part.code">{{ part.text }}</ng-container></ng-container></p>
            </div>
          </ng-container>
        </ng-container>
      </div>
      <p class="cw-muted" *ngIf="!a.streaming && !noChanges && !f.edits && f.status !== 'deleted'">No changes in this file.</p>

      <button type="button" class="cw-link" *ngIf="!a.streaming && f.after && f.edits" (click)="f.showFull = !f.showFull">
        {{ f.showFull ? 'Hide full file' : 'Show full file' }}
      </button>
      <pre *ngIf="f.after && f.showFull" class="language-{{ f.language }}"><code class="language-{{ f.language }}" [innerHTML]="f.fullHtml"></code></pre>
    </div>
  </ng-container>

  <ng-template #rows let-list>
    <div *ngFor="let row of list; trackBy: trackRow" [class.ln-add]="row.kind === 'add'" [class.ln-del]="row.kind === 'remove'" [class.ln-unchanged]="row.kind === 'context' || row.kind === 'typing'" [class.cw-typing]="row.kind === 'typing'"><span class="cw-ln">{{ row.oldLine }}</span><span class="cw-ln">{{ row.newLine }}</span><span *ngFor="let part of row.parts; trackBy: trackIndex" [class.hl]="part.changed" [innerHTML]="part.html"></span></div>
  </ng-template>
</div>
